import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, LayoutDashboard, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { prefersReducedMotion } from "@/utils/motion";
import HomeNav from "./HomeNav";
import { HERO_VIDEO_POSTER, HERO_VIDEO_SRC, WAITLIST_URL } from "./constants";

// Device-class latency, jittered like the Trackcast page's readout
const GAP_BASE = 0.3;
const GAP_JITTER = 0.06;

// Text column fades to a flat 20% tint over the footage; bottom edge melts
// into the page background so the next section can sit directly below.
const OVERLAY =
  "bg-[linear-gradient(90deg,rgba(3,7,18,0.96)_0%,rgba(3,7,18,0.85)_34%,rgba(3,7,18,0.4)_50%,rgba(3,7,18,0.2)_60%,rgba(3,7,18,0.2)_100%),linear-gradient(180deg,rgba(3,7,18,0.45)_0%,rgba(3,7,18,0)_22%,rgba(3,7,18,0)_85%,#030712_100%)]";

interface HomeHeroProps {
  signedIn: boolean;
}

export default function HomeHero({ signedIn }: HomeHeroProps) {
  const { t } = useTranslation("pages");
  const reduced = prefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [gap, setGap] = useState(GAP_BASE.toFixed(1));

  useEffect(() => {
    if (reduced) {
      videoRef.current?.pause();
      return;
    }
    const timer = setInterval(() => {
      const value = GAP_BASE + (Math.random() - 0.5) * GAP_JITTER;
      setGap(value.toFixed(2));
    }, 900);
    return () => clearInterval(timer);
  }, [reduced]);

  return (
    <header
      className="relative isolate overflow-hidden bg-gray-950 text-white"
      aria-label={t("home.heroVideoAlt")}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 -z-10 h-full w-full object-cover object-[center_35%]"
        src={HERO_VIDEO_SRC}
        poster={HERO_VIDEO_POSTER}
        autoPlay={!reduced}
        muted
        loop
        playsInline
        aria-hidden="true"
      />
      <div className={`absolute inset-0 -z-10 ${OVERLAY}`} aria-hidden="true" />

      <HomeNav />

      <div className="mx-auto grid max-w-6xl items-end gap-6 px-6 pb-20 pt-6 sm:pt-10 lg:min-h-[520px] lg:grid-cols-[minmax(0,640px)_1fr] lg:pb-24 lg:pt-12">
        {/* Broadcast chrome stays untranslated, like real timing screens */}
        <div className="order-first flex flex-nowrap items-center gap-2 font-mono text-[11px] tracking-[0.04em] lg:order-last lg:flex-col lg:items-end lg:self-start lg:justify-self-end">
          <span className="inline-flex items-center gap-1.5 rounded bg-red-500/90 px-2 py-0.5 font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-white motion-safe:animate-pulse" />
            LIVE
          </span>
          <span className="hidden rounded border border-white/15 bg-gray-950/70 px-2 py-0.5 backdrop-blur sm:inline-flex">
            #49 · SUPERRACE R4 · EVERLAND
          </span>
          <span className="inline-flex items-baseline gap-2 whitespace-nowrap rounded-md border border-white/15 bg-gray-950/70 px-2.5 py-1 backdrop-blur sm:px-3 sm:py-1.5">
            <span className="text-[10px] uppercase text-white/80 sm:text-[11px]">
              <span className="sm:hidden">{t("home.hud.gapShort")}</span>
              <span className="hidden sm:inline">{t("home.hud.gap")}</span>
            </span>
            <b className="text-lg font-semibold tabular-nums text-violet-400 sm:text-3xl">
              {gap} s
            </b>
          </span>
        </div>

        <div>
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.14em] text-gray-300">
            {t("home.eyebrow")}
          </p>
          <h1 className="mb-5 text-5xl font-bold leading-[0.98] tracking-[-0.035em] text-balance sm:text-6xl lg:text-7xl">
            {t("home.heroHeadline1")}
            <br />
            {t("home.heroHeadline2")}{" "}
            <span className="text-violet-400">{t("home.heroHeadline3")}</span>
          </h1>
          <p className="mb-7 max-w-[52ch] text-base leading-relaxed text-gray-300 sm:text-lg">
            {t("home.heroSubtext")}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {signedIn ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-100"
              >
                <LayoutDashboard className="h-4 w-4" />
                {t("home.goToDashboard")}
              </Link>
            ) : (
              <a
                href={WAITLIST_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                {t("home.joinWaitingList")}
                <ArrowRight className="h-4 w-4" />
              </a>
            )}
            <Link
              to="/sessions/all"
              className="inline-flex items-center gap-2 rounded-full border border-gray-700 px-6 py-3 text-sm font-semibold text-gray-200 transition-colors hover:border-gray-500 hover:text-white"
            >
              <Play className="h-3.5 w-3.5 fill-current" />
              {t("home.watchPublic")}
            </Link>
          </div>
          {!signedIn && (
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.04em] text-gray-400">
              {t("home.privateBetaNote")}
            </p>
          )}
        </div>
      </div>
    </header>
  );
}
